import { IdentityProvider } from "../../../../domain/IdentityProvider";
import { Button, Disclose, Typography } from "@optimizely/axiom";
import { useState } from "react";
import { ConfirmationDialog } from "../../ConfirmationDialog/ConfirmationDialog";
import { useOrganizationIdp } from "../../../../hooks/useOrganizationIdp/useOrganizationIdp";
import { useUserContext } from "../../../../providers/UserProvider";
import { emitToast } from "../../../../lib/toaster-utils";

export const SCIMDisclosure = ({ identityProvider }: { identityProvider: IdentityProvider }) => {
    const { organizationId } = useUserContext();
    const { removeSCIMForIdentityProvider } = useOrganizationIdp(organizationId);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [removeInProgress, setRemoveInProgress] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { scimApiKey = "" } = identityProvider;
    const handleRemove = () => {
        setRemoveInProgress(true);
        setError(null);
        removeSCIMForIdentityProvider({ identityProviderId: identityProvider.id })
            .then((res) => {
                if (res.error) {
                    setError(res.error);
                    return;
                }
                emitToast({
                    message: "SCIM connection removed successfully."
                });
                setShowConfirmationDialog(false);
            })
            .finally(() => {
                setRemoveInProgress(false);
            });
    };
    return (
        <>
            <Disclose title={identityProvider.idpName}>
                <div className="flex flex--column push-triple--left">
                    <Typography type="caption">SCIM Key</Typography>
                    <Typography type="xs">
                        <span style={{ color: "#707070" }}>{scimApiKey}</span>
                    </Typography>
                </div>
                <Button
                    style="danger-outline"
                    className="push-triple--top push-triple--left danger-outline"
                    onClick={() => setShowConfirmationDialog(true)}
                >
                    Remove SCIM Connection
                </Button>
            </Disclose>
            {showConfirmationDialog && (
                <ConfirmationDialog
                    error={error}
                    callToActionText="Remove"
                    loading={removeInProgress}
                    onCancel={() => setShowConfirmationDialog(false)}
                    onConfirmation={handleRemove}
                    title="Remove SCIM Connection"
                >
                    <Typography type="body">
                        You are about to remove SCIM for the following SSO Connection:
                        <p className="push-triple--ends weight--bold">{identityProvider.idpName}</p>
                        Disabling SCIM stops automatic user provisioning, updates, and deprovisioning from your identity
                        provider.
                        <ul className="warning-list">
                            <li>New users or user groups won&apos;t be auto-created.</li>
                            <li>Changes made in your IdP to users or user groups will no longer sync.</li>
                            <li>Deprovisioned users or user groups will not be automatically removed.</li>
                            <li>Users or user groups must be managed manually in the Admin Center.</li>
                        </ul>
                        No users or data will be deleted. Existing access remains unchanged until manually updated.
                    </Typography>
                </ConfirmationDialog>
            )}
        </>
    );
};

SCIMDisclosure.displayName = "SCIMDisclosure";
